import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router"
import { ArrowUpRight, Phone01 } from "@untitledui/icons"
import { toast } from "sonner"

import type { DeclineReason, Decision, QueueItem } from "@/lib/api/ghl-types"
import { useDecide, useGhlContact, useGhlQueue } from "@/lib/api/use-ghl"
import { useEstimate } from "@/lib/api/use-estimates"
import { cx } from "@/lib/utils/cx"
import { BookDialog } from "./book-dialog"
import { CALLBACK_PRESETS, DECLINE_REASONS, queueKindWord, suggestedReplies } from "./follow-up"
import { Avatar, ChannelIcon, DARK_BTN, Empty, INPUT, LIGHT_BTN, StatusPill, TagChip, fmtDateTime, relTime, usd } from "./shared"

// Today — the work queue. The same desk as the Tasks room (queue · work ·
// context) on the CRM's own objects: one thing to do per card, and every
// decision does the thing — sends, books, moves the deal, makes the next
// task — so nobody has to remember the steps.

export function Today({ teamId }: { teamId: number | string }) {
  const { data, isLoading } = useGhlQueue(teamId)
  const items = useMemo(() => data?.groups.flatMap((g) => g.items) ?? [], [data])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = items.find((i) => i.id === selectedId) ?? items[0] ?? null

  // When the selected item leaves the queue, move to the next one.
  useEffect(() => {
    if (selectedId && !items.some((i) => i.id === selectedId)) setSelectedId(items[0]?.id ?? null)
  }, [items, selectedId])

  if (isLoading && !data) return <Empty>Loading your day…</Empty>
  if (!items.length) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 px-6 py-16 text-center">
        <p className="text-[15px] font-semibold text-zinc-900">Nothing needs you right now.</p>
        <p className="mt-1 text-[13.5px] text-zinc-500">Replies, calls and visits land here as they come up. <Link to="/marketing/contacts" className="font-semibold text-zinc-700 underline decoration-zinc-300 underline-offset-2">See the contacts</Link>.</p>
      </div>
    )
  }

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[300px_minmax(0,1fr)_340px]">
      <Queue groups={data?.groups ?? []} selectedId={selected?.id ?? null} onSelect={setSelectedId} />
      {selected ? <Work key={selected.id} teamId={teamId} item={selected} onDone={() => { const i = items.findIndex((x) => x.id === selected.id); setSelectedId(items[i + 1]?.id ?? items[0]?.id ?? null) }} /> : <div />}
      {selected && <Context key={`ctx-${selected.contactId}`} teamId={teamId} item={selected} />}
    </div>
  )
}

function Queue({ groups, selectedId, onSelect }: { groups: NonNullable<ReturnType<typeof useGhlQueue>["data"]>["groups"]; selectedId: string | null; onSelect: (id: string) => void }) {
  return (
    <nav aria-label="Today's queue" className="min-w-0 rounded-xl border border-zinc-200 bg-white lg:sticky lg:top-5 lg:max-h-[calc(100dvh-8rem)] lg:overflow-y-auto">
      {groups.map((g) => (
        <section key={g.key} aria-label={g.label} className="border-b border-zinc-100 last:border-0">
          <p className="flex items-baseline justify-between px-3 pb-1 pt-3 text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">
            {g.label} <span className="tabular-nums text-zinc-400">{g.items.length}</span>
          </p>
          <ul>
            {g.items.map((it) => {
              const on = it.id === selectedId
              const overdue = new Date(it.dueAt).getTime() < new Date().setHours(0, 0, 0, 0) && it.kind !== "reply" && it.kind !== "confirm"
              return (
                <li key={it.id}>
                  <button type="button" onClick={() => onSelect(it.id)} aria-current={on} className={cx("flex w-full items-start gap-2.5 border-l-2 px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900/30", on ? "border-zinc-900 bg-zinc-50" : "border-transparent hover:bg-zinc-50/70")}>
                    <Avatar name={it.contactName} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-[13.5px] font-semibold text-zinc-900">{it.contactName}</span>
                        <span className={cx("shrink-0 text-[11.5px] tabular-nums", overdue ? "font-semibold text-red-700" : "text-zinc-400")}>{relTime(it.dueAt)}</span>
                      </span>
                      <span className="block truncate text-[12.5px] text-zinc-500">{queueKindWord(it)}{it.vehicle ? ` · ${it.vehicle}` : ""}{it.dealValue != null ? ` · ${usd(it.dealValue)}` : ""}</span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </nav>
  )
}

/* ── The work card ─────────────────────────────────────────────────────── */

function Work({ teamId, item, onDone }: { teamId: number | string; item: QueueItem; onDone: () => void }) {
  const decide = useDecide(teamId)
  const [booking, setBooking] = useState(false)
  const first = item.contactName.split(" ")[0]

  const run = async (d: Decision, fallback?: string) => {
    try {
      const r = await decide.mutateAsync(d)
      toast.success(r.note ?? fallback ?? "Done")
      onDone()
    } catch (e) {
      toast.error((e as Error)?.message || "Couldn't do that — try again")
    }
  }
  const book = async (startsAt: string, kind: "inspection" | "dropoff") => {
    await run({ kind: "book", contactId: item.contactId, taskId: item.taskId, startsAt, appointmentKind: kind })
    setBooking(false)
  }

  return (
    <section aria-label={item.title} className="min-w-0 rounded-xl border border-zinc-200 bg-white">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4">
        <div className="min-w-0">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">{queueKindWord(item)}</p>
          <h2 className="mt-0.5 text-[20px] font-semibold tracking-tight text-zinc-900">{item.contactName}</h2>
          <p className="text-[13px] text-zinc-500">{[item.vehicle, item.dealValue != null ? usd(item.dealValue) : null, item.stageName, item.ownerName].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="flex items-center gap-2">
          {item.phone && <a href={`tel:${item.phone.replace(/[^\d+]/g, "")}`} className={LIGHT_BTN}><Phone01 className="size-4" aria-hidden />{item.phone}</a>}
          <Link to={`/marketing/contacts/${item.contactId}`} className="inline-flex h-10 items-center gap-1 rounded-lg px-2 text-[13px] font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900">Open <ArrowUpRight className="size-3.5" aria-hidden /></Link>
        </div>
      </header>

      <div className="px-5 py-4">
        {item.kind === "reply" && <ReplyWork item={item} first={first} busy={decide.isPending} run={run} onBook={() => setBooking(true)} />}
        {item.kind === "confirm" && <ConfirmWork item={item} first={first} busy={decide.isPending} run={run} />}
        {(item.kind === "call" || item.kind === "callback" || item.kind === "wrapup") && <CallWork item={item} first={first} busy={decide.isPending} run={run} onBook={() => setBooking(true)} />}
        {item.kind === "visit" && <VisitWork item={item} first={first} busy={decide.isPending} run={run} />}
        {item.kind === "overdue" && <TaskWork item={item} busy={decide.isPending} run={run} />}
      </div>

      <BookDialog open={booking} teamId={teamId} contactName={item.contactName} onClose={() => setBooking(false)} onBook={book} />
    </section>
  )
}

type Run = (d: Decision, fallback?: string) => Promise<void>

function Chip({ children, onClick, tone = "log", disabled, pressed }: { children: React.ReactNode; onClick: () => void; tone?: "do" | "log" | "close"; disabled?: boolean; pressed?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      className={cx(
        "inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-full border px-4 py-2 text-left text-[14px] font-medium leading-snug transition-colors duration-150 disabled:opacity-40",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30",
        tone === "do" && "border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800",
        tone === "log" && (pressed ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-50"),
        tone === "close" && (pressed ? "border-red-700 bg-red-700 text-white" : "border-zinc-300 bg-white text-red-700 hover:bg-red-50"),
      )}
    >
      {children}
    </button>
  )
}

function Reasons({ onPick, busy }: { onPick: (r: DeclineReason) => void; busy: boolean }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Why">
      {DECLINE_REASONS.map((r) => <Chip key={r.value} onClick={() => onPick(r.value)} disabled={busy}>{r.label}</Chip>)}
    </div>
  )
}

function Callbacks({ onPick, busy }: { onPick: (iso: string) => void; busy: boolean }) {
  const [custom, setCustom] = useState("")
  const at = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); d.setHours(10, 0, 0, 0); return d.toISOString() }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" role="group" aria-label="When">
      {CALLBACK_PRESETS.map((p) => <Chip key={p.label} onClick={() => onPick(at(p.days))} disabled={busy}>{p.label}</Chip>)}
      <input type="date" value={custom} onChange={(e) => setCustom(e.target.value)} aria-label="Pick a date" className={cx(INPUT, "h-11")} />
      <Chip onClick={() => custom && onPick(new Date(`${custom}T10:00:00`).toISOString())} disabled={!custom || busy}>Set</Chip>
    </div>
  )
}

function Note({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder="Add a note (optional)" aria-label="Note" rows={2} className="mt-3 w-full resize-none rounded-lg border border-zinc-300 px-3 py-2 text-[13.5px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10" />
}

function ReplyWork({ item, first, busy, run, onBook }: { item: QueueItem; first: string; busy: boolean; run: Run; onBook: () => void }) {
  const [body, setBody] = useState("")
  const suggestions = suggestedReplies(item.lastInbound?.body, first)
  return (
    <div>
      {item.lastInbound && (
        <blockquote className="rounded-xl bg-zinc-50 px-4 py-3">
          <p className="text-[16px] leading-relaxed text-zinc-900">“{item.lastInbound.body}”</p>
          <footer className="mt-1 text-[12px] text-zinc-500">{first} · {fmtDateTime(item.lastInbound.at)}</footer>
        </blockquote>
      )}
      <p className="mt-4 text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">One-tap replies</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {suggestions.map((s) => <Chip key={s} onClick={() => setBody(s)} pressed={body === s}>{s}</Chip>)}
      </div>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && body.trim()) void run({ kind: "reply", contactId: item.contactId, body: body.trim() }, "Sent") }} placeholder={item.smsBlocked ? "They opted out of texts — call instead." : `Reply to ${first}…`} disabled={item.smsBlocked} aria-label="Reply" rows={3} className="mt-3 w-full resize-none rounded-lg border border-zinc-300 px-3 py-2 text-[14px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 disabled:bg-zinc-50" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => void run({ kind: "reply", contactId: item.contactId, body: body.trim() }, "Sent")} disabled={!body.trim() || busy || item.smsBlocked} className={DARK_BTN}>Send</button>
        <button type="button" onClick={onBook} className={LIGHT_BTN}>Book {first}</button>
        <button type="button" onClick={() => void run({ kind: "stop_texts", contactId: item.contactId })} disabled={busy} className="ml-auto h-10 rounded-lg px-3 text-[13px] font-semibold text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900">Stop the texts</button>
      </div>
    </div>
  )
}

function ConfirmWork({ item, first, busy, run }: { item: QueueItem; first: string; busy: boolean; run: Run }) {
  const { data: est } = useEstimate(item.estimateId)
  const [open, setOpen] = useState<null | "reasons">(null)
  const lines = est?.lineItems ?? []
  return (
    <div>
      <p className="text-[14px] text-zinc-700">A new estimate for {first} is waiting. Confirm it to start the follow-up — the first call lands in this queue.</p>
      {est && (
        <div className="mt-4 rounded-xl border border-zinc-200">
          <div className="flex items-baseline justify-between px-4 py-3">
            <p className="text-[18px] font-semibold tabular-nums text-zinc-900">{usd(est.min)} – {usd(est.max)}</p>
            <p className="text-[12.5px] text-zinc-500">{lines.length} {lines.length === 1 ? "part" : "parts"} · {est.images?.length ?? 0} photos</p>
          </div>
          <ul className="divide-y divide-zinc-100 border-t border-zinc-100">
            {lines.slice(0, 5).map((li) => (
              <li key={li.id} className="flex items-center justify-between gap-3 px-4 py-2 text-[13.5px]">
                <span className="text-zinc-800">{li.component.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}</span>
                <span className="text-zinc-500">{li.action}</span>
              </li>
            ))}
            {lines.length > 5 && <li className="px-4 py-2 text-[12.5px] text-zinc-500">+{lines.length - 5} more</li>}
          </ul>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Chip tone="do" onClick={() => item.estimateId != null && void run({ kind: "confirm", contactId: item.contactId, estimateId: item.estimateId })} disabled={busy || item.estimateId == null}>Confirm and start follow-up</Chip>
        {item.estimateId != null && <Link to={`/estimates/${item.estimateId}/edit`} className={LIGHT_BTN}>Open to edit</Link>}
        <Chip tone="close" pressed={open === "reasons"} onClick={() => setOpen(open ? null : "reasons")}>Not a real job</Chip>
      </div>
      {open === "reasons" && item.estimateId != null && <Reasons busy={busy} onPick={(reason) => void run({ kind: "not_real", contactId: item.contactId, estimateId: item.estimateId!, reason })} />}
    </div>
  )
}

function CallWork({ item, first, busy, run, onBook }: { item: QueueItem; first: string; busy: boolean; run: Run; onBook: () => void }) {
  const [open, setOpen] = useState<null | "callback" | "reasons" | "number" | "close">(null)
  const [note, setNote] = useState("")
  const [phone, setPhone] = useState("")
  const n = note.trim() || null
  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[13.5px] text-zinc-700">
        {item.attempt != null && <span>Call {item.attempt} of 4</span>}
        {item.estimateStatus && <span>Estimate {item.estimateStatus === "InProgress" ? "in progress" : item.estimateStatus.toLowerCase()}</span>}
        {item.kind === "wrapup" && <span className="font-semibold text-zinc-900">Last call — close it or keep trying.</span>}
      </div>
      {item.lastInbound && (
        <blockquote className="mt-3 rounded-xl bg-zinc-50 px-4 py-3">
          <p className="text-[14px] leading-relaxed text-zinc-900">“{item.lastInbound.body}”</p>
          <footer className="mt-1 text-[12px] text-zinc-500">{first} · {relTime(item.lastInbound.at)}</footer>
        </blockquote>
      )}

      <p className="mt-5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">Reached {first}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Chip tone="do" onClick={onBook} disabled={busy}>Booked — pick a time</Chip>
        <Chip onClick={() => void run({ kind: "reached", contactId: item.contactId, taskId: item.taskId, then: "thinking", note: n })} disabled={busy}>Still thinking</Chip>
        <Chip onClick={() => void run({ kind: "reached", contactId: item.contactId, taskId: item.taskId, then: "changes", note: n })} disabled={busy}>Wants changes</Chip>
        <Chip pressed={open === "callback"} onClick={() => setOpen(open === "callback" ? null : "callback")} disabled={busy}>Call back on…</Chip>
        <Chip tone="close" pressed={open === "reasons"} onClick={() => setOpen(open === "reasons" ? null : "reasons")} disabled={busy}>Not interested</Chip>
      </div>
      {open === "callback" && <Callbacks busy={busy} onPick={(iso) => void run({ kind: "reached", contactId: item.contactId, taskId: item.taskId, then: "callback", callbackAt: iso, note: n })} />}
      {open === "reasons" && <Reasons busy={busy} onPick={(reason) => void run({ kind: "reached", contactId: item.contactId, taskId: item.taskId, then: "not_interested", reason, note: n })} />}

      <p className="mt-5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">Didn't reach {first}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Chip onClick={() => void run({ kind: "no_answer", contactId: item.contactId, taskId: item.taskId })} disabled={busy}>No answer</Chip>
        <Chip pressed={open === "number"} onClick={() => setOpen(open === "number" ? null : "number")} disabled={busy}>Wrong number</Chip>
        {item.kind === "wrapup" && <Chip tone="close" pressed={open === "close"} onClick={() => setOpen(open === "close" ? null : "close")} disabled={busy}>Close it</Chip>}
      </div>
      {open === "number" && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(313) 555-0000" aria-label="New phone number" className={cx(INPUT, "h-11 w-48")} />
          <Chip onClick={() => phone.trim() && void run({ kind: "wrong_number", contactId: item.contactId, taskId: item.taskId, phone: phone.trim() })} disabled={!phone.trim() || busy}>Save and call again</Chip>
        </div>
      )}
      {open === "close" && <Reasons busy={busy} onPick={(reason) => void run({ kind: "close", contactId: item.contactId, taskId: item.taskId, reason })} />}
      <Note value={note} onChange={setNote} />
    </div>
  )
}

function VisitWork({ item, first, busy, run }: { item: QueueItem; first: string; busy: boolean; run: Run }) {
  return (
    <div>
      <p className="text-[14px] text-zinc-700">{item.appointmentAt ? fmtDateTime(item.appointmentAt) : "Today"} · {item.title.split(" — ")[0]}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        <Chip tone="do" onClick={() => item.appointmentId && void run({ kind: "visit", contactId: item.contactId, appointmentId: item.appointmentId, outcome: "showed" })} disabled={busy}>Yes, {first} came in</Chip>
        <Chip onClick={() => item.appointmentId && void run({ kind: "visit", contactId: item.contactId, appointmentId: item.appointmentId, outcome: "noshow" })} disabled={busy}>Didn't show</Chip>
      </div>
      <p className="mt-3 text-[12.5px] text-zinc-500">A no-show texts them and puts a rebook in this queue.</p>
    </div>
  )
}

function TaskWork({ item, busy, run }: { item: QueueItem; busy: boolean; run: Run }) {
  return (
    <div>
      <p className="text-[16px] font-medium text-zinc-900">{item.title}</p>
      <p className="mt-1 text-[13px] text-zinc-500">Due {relTime(item.dueAt)}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        <Chip tone="do" onClick={() => item.taskId && void run({ kind: "done", taskId: item.taskId }, "Done")} disabled={busy}>Done</Chip>
        {CALLBACK_PRESETS.map((p) => <Chip key={p.label} onClick={() => item.taskId && void run({ kind: "snooze", taskId: item.taskId, days: p.days })} disabled={busy}>{p.label}</Chip>)}
      </div>
    </div>
  )
}

/* ── Context rail ──────────────────────────────────────────────────────── */

function Context({ teamId, item }: { teamId: number | string; item: QueueItem }) {
  const { data } = useGhlContact(teamId, item.contactId)
  const c = data?.contact
  const recent = useMemo(() => {
    if (!data) return []
    const ev = [
      ...data.messages.map((m) => ({ id: m.id, at: m.dateAdded, icon: <ChannelIcon type={m.type} className="size-3.5 text-zinc-400" />, text: m.direction === "inbound" ? `${item.contactName.split(" ")[0]}: ${m.body}` : m.body, status: m.direction === "outbound" && m.type !== "TYPE_CALL" ? m.status : null })),
      ...data.notes.map((n) => ({ id: n.id, at: n.dateAdded, icon: <span className="text-[10px] font-bold text-zinc-400">N</span>, text: n.body, status: null })),
    ]
    return ev.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, 6)
  }, [data, item.contactName])

  return (
    <aside aria-label="About this contact" className="min-w-0 space-y-5 rounded-xl border border-zinc-200 bg-white px-4 py-4 xl:sticky xl:top-5">
      <div>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">Contact</p>
        <dl className="mt-1.5 space-y-1 text-[13px]">
          {c?.phone && <div className="flex justify-between gap-3"><dt className="text-zinc-500">Phone</dt><dd className="text-zinc-900">{c.phone}</dd></div>}
          {c?.email && <div className="flex justify-between gap-3"><dt className="text-zinc-500">Email</dt><dd className="truncate text-zinc-900">{c.email}</dd></div>}
          {item.ownerName && <div className="flex justify-between gap-3"><dt className="text-zinc-500">Owner</dt><dd className="text-zinc-900">{item.ownerName}</dd></div>}
          {c && <div className="flex justify-between gap-3"><dt className="text-zinc-500">Texts</dt><dd className={c.dndSettings.SMS.status === "active" ? "font-semibold text-red-700" : "text-zinc-900"}>{c.dndSettings.SMS.status === "active" ? "Opted out" : c.phone ? "OK" : "No number"}</dd></div>}
        </dl>
        {c && c.tags.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{c.tags.map((t) => <TagChip key={t} tag={t} />)}</div>}
      </div>
      {(item.dealValue != null || item.estimateId != null) && (
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">Deal</p>
          <p className="mt-1 text-[16px] font-semibold tabular-nums text-zinc-900">{item.dealValue != null ? usd(item.dealValue) : "—"}</p>
          <p className="text-[12.5px] text-zinc-500">{[item.stageName, item.estimateStatus ? `estimate ${item.estimateStatus === "InProgress" ? "in progress" : item.estimateStatus.toLowerCase()}` : null].filter(Boolean).join(" · ")}</p>
          {item.estimateId != null && <Link to={`/estimates/${item.estimateId}`} className="mt-1 inline-flex items-center gap-1 text-[12.5px] font-semibold text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900">Estimate #{item.estimateId} <ArrowUpRight className="size-3" aria-hidden /></Link>}
        </div>
      )}
      <div>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">Recently</p>
        {recent.length === 0 ? <p className="mt-1 text-[13px] text-zinc-500">Nothing yet.</p> : (
          <ol className="mt-1.5 divide-y divide-zinc-100">
            {recent.map((e) => (
              <li key={e.id} className="flex gap-2 py-1.5">
                <span className="mt-1 grid size-4 shrink-0 place-items-center">{e.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12.5px] text-zinc-800">{e.text}</span>
                  <span className="flex items-center gap-1.5 text-[11px] text-zinc-400">{relTime(e.at)}{e.status && <StatusPill status={e.status} />}</span>
                </span>
              </li>
            ))}
          </ol>
        )}
        <Link to={`/marketing/contacts/${item.contactId}`} className="mt-2 inline-block text-[12.5px] font-semibold text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900">Full record</Link>
      </div>
    </aside>
  )
}
